type PendingSync = {
  requestKey: string
  signal: AbortSignal
  result: Promise<boolean>
}

type DeviceQueue = {
  tail: Promise<unknown>
  pending: Set<PendingSync>
}

/** Document-local only. Backend locks still own ordering between documents/devices. */
export class DeviceSyncCoordinator {
  private readonly devices = new Map<string, DeviceQueue>()

  synchronize(
    deviceKey: string,
    requestKey: string,
    signal: AbortSignal,
    run: () => Promise<boolean>,
  ): Promise<boolean> {
    if (signal.aborted) {
      return Promise.resolve(false)
    }
    let queue = this.devices.get(deviceKey)
    if (!queue) {
      queue = { tail: Promise.resolve(), pending: new Set() }
      this.devices.set(deviceKey, queue)
    }

    for (const pending of queue.pending) {
      if (queue.tail === pending.result && pending.requestKey === requestKey && !pending.signal.aborted) {
        return pending.result.then((result) => {
          if (signal.aborted) {
            return false
          }
          // A remount may cancel the original observer after we joined it. Its
          // HTTP has now settled, so let the still-current observer register.
          return pending.signal.aborted ? this.synchronize(deviceKey, requestKey, signal, run) : result
        })
      }
    }

    const result = queue.tail.catch(() => undefined).then(() => (signal.aborted ? false : run()))
    const pending = { requestKey, signal, result }
    queue.pending.add(pending)
    queue.tail = result
    const release = () => {
      queue.pending.delete(pending)
      if (queue.pending.size === 0 && this.devices.get(deviceKey) === queue) {
        this.devices.delete(deviceKey)
      }
    }
    void result.then(release, release)
    return result
  }
}

// Module lifetime outlives route-group providers, but no credentials are persisted.
export const deviceSyncCoordinator = new DeviceSyncCoordinator()
